export class ReportGenerator {
  // Genera informe de carpetas con conteo de archivos y tamaño (con profundidad configurable)
  static generateFolderReport(data, maxDepth = 2) {
    const lines = [];
    const stats = this.getDetailedStats(data);

    const processNode = (node, depth = 0) => {
      if (depth > maxDepth) return; // 🔥 Límite de profundidad

      const indent = "  ".repeat(depth);
      const prefix = depth === 0 ? "" : "├── ";

      if (node.type === "directory") {
        let status = "";

        if (node.fileCount === 0) {
          status = " (VACÍA)";
        } else {
          status = ` (${node.fileCount} archivos - ${node.sizeReadable})`;
        }

        lines.push(`${indent}${prefix}${node.name}${status}`);

        node.children
          .filter((child) => child.type === "directory")
          .forEach((child) => processNode(child, depth + 1));
      }
    };

    lines.push(`INFORME DE CARPETAS - ${new Date().toLocaleString()}`);
    lines.push("=".repeat(60));
    lines.push("");

    processNode(data);

    lines.push("");
    lines.push("RESUMEN GENERAL:");
    lines.push(`- Total de carpetas: ${stats.totalDirectories}`);
    lines.push(`- Carpetas vacías: ${stats.emptyDirectories}`);
    lines.push(`- Total de archivos: ${stats.totalFiles}`);
    lines.push(`- Tamaño total: ${stats.totalSizeReadable}`);
    lines.push("");
    lines.push("DESGLOSE POR TIPO DE ARCHIVO:");

    const sortedTypes = Object.entries(stats.fileTypes).sort(([, a], [, b]) => b - a);

    if (sortedTypes.length === 0) {
      lines.push("- No se encontraron archivos");
    } else {
      sortedTypes.forEach(([ext, count]) => {
        const fileType = this.getFileTypeDescription(ext);
        lines.push(`- ${ext.toUpperCase()}: ${count} archivo${count !== 1 ? "s" : ""} (${fileType})`);
      });
    }

    return lines.join("\n");
  }

  // Genera informe completo con estructura de archivos (sin límite de profundidad)
  static generateCompleteStructure(data) {
    const lines = [];

    const processNode = (node, depth = 0, isLast = true, parentPrefix = "") => {
      const currentPrefix = depth === 0 ? "" : isLast ? "└── " : "├── ";
      const nextParentPrefix = depth === 0 ? "" : parentPrefix + (isLast ? "    " : "│   ");

      let displayName = node.name;
      if (node.type === "file") {
        const ext = this.getFileExtension(node.name);
        const fileType = this.getFileTypeDescription(ext);
        displayName = `${node.name} (${fileType})`;
      }

      lines.push(`${parentPrefix}${currentPrefix}${displayName}`);

      if (node.type === "directory" && node.children.length > 0) {
        node.children.forEach((child, index) => {
          const isLastChild = index === node.children.length - 1;
          processNode(child, depth + 1, isLastChild, nextParentPrefix);
        });
      }
    };

    const stats = this.getDetailedStats(data);

    lines.push(`ESTRUCTURA COMPLETA CON TIPOS DE ARCHIVO - ${new Date().toLocaleString()}`);
    lines.push("=".repeat(60));
    lines.push("");

    processNode(data);

    lines.push("");
    lines.push("RESUMEN GENERAL:");
    lines.push(`- Total de carpetas: ${stats.totalDirectories}`);
    lines.push(`- Carpetas vacías: ${stats.emptyDirectories}`);
    lines.push(`- Total de archivos: ${stats.totalFiles}`);
    lines.push(`- Tamaño total: ${stats.totalSizeReadable}`);
    lines.push("");
    lines.push("DESGLOSE POR TIPO DE ARCHIVO:");

    const sortedTypes = Object.entries(stats.fileTypes).sort(([, a], [, b]) => b - a);

    if (sortedTypes.length === 0) {
      lines.push("- No se encontraron archivos");
    } else {
      sortedTypes.forEach(([ext, count]) => {
        const fileType = this.getFileTypeDescription(ext);
        lines.push(`- ${ext.toUpperCase()}: ${count} archivo${count !== 1 ? "s" : ""} (${fileType})`);
      });
    }

    return lines.join("\n");
  }

  // Obtiene estadísticas detalladas incluyendo tipos de archivo y tamaños
  static getDetailedStats(node) {
    const stats = {
      totalDirectories: 0,
      emptyDirectories: 0,
      totalFiles: 0,
      totalSize: 0,
      totalSizeReadable: "0 B",
      fileTypes: {},
    };

    const processNode = (currentNode) => {
      if (currentNode.type === "directory") {
        stats.totalDirectories++;
        stats.totalSize += currentNode.size || 0;

        if (currentNode.fileCount === 0) {
          stats.emptyDirectories++;
        }

        currentNode.children.forEach((child) => processNode(child));
      } else if (currentNode.type === "file") {
        stats.totalFiles++;
        stats.totalSize += currentNode.size || 0;
        const ext = this.getFileExtension(currentNode.name);
        stats.fileTypes[ext] = (stats.fileTypes[ext] || 0) + 1;
      }
    };

    processNode(node);

    stats.totalSizeReadable = this.formatBytes(stats.totalSize);

    return stats;
  }

  static getFileExtension(filename) {
    const lastDot = filename.lastIndexOf(".");
    if (lastDot === -1 || lastDot === filename.length - 1) {
      return "sin extensión";
    }
    return filename.substring(lastDot + 1).toLowerCase();
  }

  static getFileTypeDescription(extension) {
    const typeMap = {
      pdf: "Documento PDF",
      doc: "Documento Word",
      docx: "Documento Word",
      xls: "Hoja de cálculo Excel",
      xlsx: "Hoja de cálculo Excel",
      ppt: "Presentación PowerPoint",
      pptx: "Presentación PowerPoint",
      txt: "Archivo de texto",
      jpg: "Imagen JPEG",
      jpeg: "Imagen JPEG",
      png: "Imagen PNG",
      gif: "Imagen GIF",
      bmp: "Imagen BMP",
      tiff: "Imagen TIFF",
      mp4: "Video MP4",
      avi: "Video AVI",
      mov: "Video MOV",
      mp3: "Audio MP3",
      wav: "Audio WAV",
      zip: "Archivo comprimido",
      rar: "Archivo comprimido",
      "7z": "Archivo comprimido",
      html: "Página web",
      css: "Hoja de estilos",
      js: "JavaScript",
      json: "Datos JSON",
      xml: "Documento XML",
      csv: "Datos CSV",
      "sin extensión": "Archivo sin extensión",
    };

    return typeMap[extension] || `Archivo ${extension.toUpperCase()}`;
  }

  static formatFileTypes(fileTypes) {
    const entries = Object.entries(fileTypes);
    if (entries.length === 0) return "";

    if (entries.length <= 3) {
      return entries.map(([ext, count]) => `${count} ${ext.toUpperCase()}`).join(", ");
    } else {
      const first3 = entries
        .slice(0, 3)
        .map(([ext, count]) => `${count} ${ext.toUpperCase()}`)
        .join(", ");
      const remaining = entries.length - 3;
      return `${first3}, +${remaining} más`;
    }
  }

  static formatBytes(bytes) {
    if (bytes === 0) return "0 B";

    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  static countDirectories(node) {
    if (node.type !== "directory") return 0;

    return (
      1 +
      node.children
        .filter((child) => child.type === "directory")
        .reduce((count, child) => count + this.countDirectories(child), 0)
    );
  }

  static countEmptyDirectories(node) {
    if (node.type !== "directory") return 0;

    const isEmpty = node.fileCount === 0;
    const childEmptyCount = node.children
      .filter((child) => child.type === "directory")
      .reduce((count, child) => count + this.countEmptyDirectories(child), 0);

    return (isEmpty ? 1 : 0) + childEmptyCount;
  }

  static countTotalFiles(node) {
    if (node.type === "file") return 1;

    return node.children.reduce((count, child) => count + this.countTotalFiles(child), 0);
  }
}
